import type { OptionalIfEmpty } from '@gonfalon/types/OptionalIfEmpty';
import type { Prettify } from '@gonfalon/types/Prettify';
import { queryOptions } from '@tanstack/react-query';
import type { FetchResponse } from 'openapi-fetch';
import type { MediaType } from 'openapi-typescript-helpers';

import { makeQueryId } from './makeQueryId';
import { reactQueryResponseAdapter } from './reactQueryResponseAdapter';

type OpenAPIResponse<T extends Record<string | number, unknown>, Options, Media extends MediaType> = NonNullable<
  FetchResponse<T, Options, Media>['data']
>;

type QueryKey<Input> = readonly [{ type: string } & Input];

/**
 * Create a set of query options to be shared for all instances of a given query function.
 *
 * The query will be cached in a way to correctly encodes all dependencies to that function.
 * Importantly, the query key and query function will be intertwined correctly for you, and
 * your query will therefore be cached correctly.
 *
 * You may specify additional options in the package.
 *
 * You may also safely compose these options with additional options in consumer code, in
 * case you need to tweak certain aspects of the query for your specific use case.
 */
export function createQueryOptions<
  T extends Record<string | number, unknown>,
  Options,
  Media extends MediaType,
  Input,
  Data = OpenAPIResponse<T, Options, Media>,
>(
  fetcher: (input: Input, context: { signal: AbortSignal }) => Promise<FetchResponse<T, Options, Media>>,
  options: Omit<
    ReturnType<typeof queryOptions<OpenAPIResponse<T, Options, Media>, unknown, Data, QueryKey<Input>>>,
    'queryKey' | 'queryFn'
  > = {},
) {
  const type = makeQueryId(fetcher.name);

  function factory(...args: OptionalIfEmpty<Input>) {
    const input = (args[0] ?? {}) as Input;
    return queryOptions({
      queryKey: [{ type, ...input }] as const,
      queryFn: async (context) => reactQueryResponseAdapter(fetcher(input, context)),
      ...options,
    });
  }

  function partialQueryKey(): readonly [{ type: string }];
  function partialQueryKey<const PartialArgs extends Partial<Input>>(
    partialArgs: PartialArgs,
  ): readonly [Prettify<{ type: string } & PartialArgs>];
  function partialQueryKey(
    /* We're OK with any here since the implementation signature is internal. */
    /* oxlint-disable-next-line typescript/no-explicit-any */
    partialArgs?: any,
  ) {
    return [
      {
        ...partialArgs,
        type,
      },
    ] as const;
  }

  factory.partialQueryKey = partialQueryKey;

  return factory;
}
